import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ErrorDisplay from './ErrorDisplay';
import {
  PASSWORD_MIN_LENGTH,
  PASSWORD_COMPLEXITY_REGEX,
} from '../../lib/passwordPolicy';

interface RegisterFormProps {
  onSuccess?: () => void;
  redirectTo?: string;
}

const RegisterForm: React.FC<RegisterFormProps> = ({
  onSuccess,
  redirectTo = '/admin/dashboard'
}) => {
  const { register, isLoading, error, clearError, isAuthenticated, retryLastAction } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phone: '',
  });

  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Clear error when component mounts
  useEffect(() => {
    clearError();
  }, [clearError]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(redirectTo);
    }
  }, [isAuthenticated, navigate, redirectTo]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Username validation
    if (!formData.username) {
      errors.username = 'Le nom d\'utilisateur est requis';
    } else if (formData.username.length < 3) {
      errors.username = 'Le nom d\'utilisateur doit contenir au moins 3 caractères';
    } else if (!/^[a-zA-Z0-9_-]+$/.test(formData.username)) {
      errors.username = 'Le nom d\'utilisateur ne peut contenir que des lettres, chiffres, tirets et underscores';
    }

    // Email validation
    if (!formData.email) {
      errors.email = 'L\'adresse email est requise';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'L\'adresse email n\'est pas valide';
    }

    // Password validation
    if (!formData.password) {
      errors.password = 'Le mot de passe est requis';
    } else if (formData.password.length < PASSWORD_MIN_LENGTH) {
      errors.password = `Le mot de passe doit contenir au moins ${PASSWORD_MIN_LENGTH} caractères`;
    } else if (!PASSWORD_COMPLEXITY_REGEX.test(formData.password)) {
      errors.password = 'Le mot de passe doit contenir au moins une majuscule, une minuscule et un chiffre';
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      errors.confirmPassword = 'La confirmation du mot de passe est requise';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }

    // First name validation
    if (!formData.firstName) {
      errors.firstName = 'Le prénom est requis';
    } else if (formData.firstName.length < 2) {
      errors.firstName = 'Le prénom doit contenir au moins 2 caractères';
    }

    // Last name validation
    if (!formData.lastName) {
      errors.lastName = 'Le nom est requis';
    } else if (formData.lastName.length < 2) {
      errors.lastName = 'Le nom doit contenir au moins 2 caractères';
    }

    // Phone validation (optional)
    if (formData.phone && !/^[+]?[0-9\s\-()]{10,}$/.test(formData.phone)) {
      errors.phone = 'Le numéro de téléphone n\'est pas valide';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const { confirmPassword, ...registerData } = formData;
      const { requiresVerification } = await register(registerData);
      if (requiresVerification) {
        setIsSubmitted(true);
      } else {
        onSuccess?.();
      }
    } catch {
      // Error is handled by the auth context
    }
  };

  if (isSubmitted) {
    return (
      <div className="auth-background">
        <div className="auth-background-overlay"></div>
        <div className="auth-card">
          <div className="auth-header">
            <div className="auth-logo">
              <img src="/logo-commission-fr.png" alt="CNCLT" />
            </div>
            <p className="auth-subtitle text-gray-600">Vérifiez votre email</p>
          </div>

          <div className="auth-alert auth-alert-success">
            <p>
              Votre compte a été créé. Un email de vérification a été envoyé à{' '}
              <strong>{formData.email}</strong>. Veuillez vérifier votre boîte de réception
              puis vous connecter.
            </p>
          </div>

          <div className="text-center">
            <Link to="/login" className="auth-button-primary block">
              Se connecter
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-background">
      <div className="auth-background-overlay"></div>
      <div className="auth-card">
        <div className="auth-header">
          <div className="auth-logo">
            <img src="/logo-commission-fr.png" alt="CNCLT logo" className="h-24 w-auto mx-auto brightness-0 opacity-90" />
          </div>
          <p className="auth-subtitle text-gray-600">Créez votre compte</p>
        </div>

        <ErrorDisplay
          error={error}
          onRetry={retryLastAction}
          onDismiss={clearError}
        />

        <form className="auth-form" onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="auth-form-group">
              <label className="auth-form-label">Prénom</label>
              <input
                type="text"
                name="firstName"
                className={`auth-form-input ${validationErrors.firstName ? 'auth-form-input-error' : ''}`}
                placeholder="Votre prénom"
                value={formData.firstName}
                onChange={handleInputChange}
                required
                disabled={isLoading}
              />
              {validationErrors.firstName && (
                <p className="auth-form-error">{validationErrors.firstName}</p>
              )}
            </div>

            <div className="auth-form-group">
              <label className="auth-form-label">Nom</label>
              <input
                type="text"
                name="lastName"
                className={`auth-form-input ${validationErrors.lastName ? 'auth-form-input-error' : ''}`}
                placeholder="Votre nom"
                value={formData.lastName}
                onChange={handleInputChange}
                required
                disabled={isLoading}
              />
              {validationErrors.lastName && (
                <p className="auth-form-error">{validationErrors.lastName}</p>
              )}
            </div>
          </div>

          <div className="auth-form-group">
            <label className="auth-form-label">Nom d'utilisateur</label>
            <input
              type="text"
              name="username"
              className={`auth-form-input ${validationErrors.username ? 'auth-form-input-error' : ''}`}
              placeholder="nom_utilisateur"
              value={formData.username}
              onChange={handleInputChange}
              required
              disabled={isLoading}
            />
            {validationErrors.username && (
              <p className="auth-form-error">{validationErrors.username}</p>
            )}
          </div>

          <div className="auth-form-group">
            <label className="auth-form-label">Adresse email</label>
            <input
              type="email"
              name="email"
              className={`auth-form-input ${validationErrors.email ? 'auth-form-input-error' : ''}`}
              placeholder="votre@email.com"
              value={formData.email}
              onChange={handleInputChange}
              required
              disabled={isLoading}
            />
            {validationErrors.email && (
              <p className="auth-form-error">{validationErrors.email}</p>
            )}
          </div>

          <div className="auth-form-group">
            <label className="auth-form-label">Téléphone (optionnel)</label>
            <input
              type="tel"
              name="phone"
              className={`auth-form-input ${validationErrors.phone ? 'auth-form-input-error' : ''}`}
              placeholder="+212 6 12 34 56 78"
              value={formData.phone}
              onChange={handleInputChange}
              disabled={isLoading}
            />
            {validationErrors.phone && (
              <p className="auth-form-error">{validationErrors.phone}</p>
            )}
          </div>

          <div className="auth-form-group">
            <label className="auth-form-label">Mot de passe</label>
            <input
              type="password"
              name="password"
              className={`auth-form-input ${validationErrors.password ? 'auth-form-input-error' : ''}`}
              placeholder="••••••••"
              value={formData.password}
              onChange={handleInputChange}
              required
              disabled={isLoading}
            />
            {validationErrors.password && (
              <p className="auth-form-error">{validationErrors.password}</p>
            )}
          </div>

          <div className="auth-form-group">
            <label className="auth-form-label">Confirmer le mot de passe</label>
            <input
              type="password"
              name="confirmPassword"
              className={`auth-form-input ${validationErrors.confirmPassword ? 'auth-form-input-error' : ''}`}
              placeholder="••••••••"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              required
              disabled={isLoading}
            />
            {validationErrors.confirmPassword && (
              <p className="auth-form-error">{validationErrors.confirmPassword}</p>
            )}
          </div>

          <button
            type="submit"
            className={`auth-button-primary ${isLoading ? 'auth-loading' : ''}`}
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <div className="auth-spinner mr-2"></div>
                Création du compte...
              </div>
            ) : (
              'Créer le compte'
            )}
          </button>

          <div className="auth-divider">
            <span className="auth-divider-text">ou</span>
          </div>

          <Link to="/login" className="auth-button-outline block text-center">
            Se connecter
          </Link>
        </form>
      </div>
    </div>
  );
};

export default RegisterForm;